// * Import react hooks
import { useState, useCallback } from "react";
//* Import react-query hooks for fetching data
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
//? API functions for applications
import { applicationAPI } from "./applicationAPI";
//? Types for pagination
import { type Pagination } from "../../services/stores/types";
//? Auxiliary types for relationships
import {
  ApplicationCompleted,
  type ApplicationDetails,
  type CreateApplicationInput,
  type UpdateApplicationInput,
} from "./types/types";

/**
 * ? Custom hook `useApplications` to manage operations related to applications.
 *
 * @param {string} token - Authentication token for API requests.
 * @returns {Object} The hook returns data, loading state, errors, and functions to interact with the API.
 */
export const useApplications = (token: string) => {
  const queryClient = useQueryClient();

  // * States for search and pagination
  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  // * Define the query key
  const queryKey = [
    "applications",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  // ? Fetch applications with react-query
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      applicationAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // * Extract data and pagination
  const applications = data?.applications || [];
  const fetchedPagination = data?.pagination || pagination;

  // ! Handlers for search and pagination
  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  // * Mutations for creating, updating, and deleting applications
  const createApplicationMutation = useMutation({
    mutationFn: (applicationData: CreateApplicationInput) =>
      applicationAPI.create(applicationData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] });
    },
  });

  const updateApplicationMutation = useMutation({
    mutationFn: ({ id, applicationData }: UpdateApplicationInput) =>
      applicationAPI.update(id, applicationData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] });
    },
  });

  const deleteApplicationMutation = useMutation({
    mutationFn: (id: string) => applicationAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] });
    },
  });

  // TODO: Fetch details of an application by ID
  const getApplicationById = useCallback(
    async (id: string): Promise<ApplicationDetails | null> => {
      try {
        const applicationDetails = await applicationAPI.getById(id, token);
        return applicationDetails;
      } catch (err) {
        console.error("Error fetching application by ID:", err);
        return null;
      }
    },
    [token]
  );

  // * Mutation for generating diagram
  const generateDiagramMutation = useMutation({
    mutationFn: ({
      id,
      diagramId,
      answerSheetsId,
    }: {
      id: string;
      diagramId: string;
      answerSheetsId: string;
    }) => applicationAPI.generate(id, diagramId, answerSheetsId, token),
  });

   const getFullApplication = useCallback(
     async (
       id: string,
       diagramId: string,
       token: string,
       answerSheetsId?: string,
       studentId?: string
     ): Promise<any | null> => {
       try {
         const applicationDetails = await applicationAPI.getByFull(
           id,
           diagramId,
           token,
           answerSheetsId || "",
           studentId
         );
         return applicationDetails;
       } catch (err) {
         console.error("Error fetching application by ID:", err);
         return null;
       }
     },
     [token]
   );

  // * Return values and functions
  return {
    applications,
    loading: isLoading || isFetching,
    error: isError ? (error as Error)?.message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createApplication: createApplicationMutation.mutate,
    updateApplication: updateApplicationMutation.mutate,
    deleteApplication: deleteApplicationMutation.mutate,
    getApplicationById,
    getFullApplication,
    changePage,
    isCreating: createApplicationMutation.isPending,
    isUpdating: updateApplicationMutation.isPending,
    isDeleting: deleteApplicationMutation.isPending,
    generateDiagram: generateDiagramMutation.mutateAsync,
    isGeneratingDiagram: generateDiagramMutation.isPending,
  };
};
